import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { fmt } from '../../lib/formatters';
import { toast, confirmar } from '../../lib/toast';

// ───────────────────────────────────────────────────────────────────────
// Tipos
// ───────────────────────────────────────────────────────────────────────

type CondicionRow = {
  id: string;
  marca_id: string;
  gama_id: string | null;
  categoria_id: string | null;
  subcategoria_id: string | null;
  margen_pct: number | null;
  rappel_pct: number | null;
  marketing_pct: number | null;
  foc_target_pct: number | null;
  comision_pm_pct: number | null;
  financiero_pct: number | null;
  margen_target_pvpr_pct: number | null;
  notas: string | null;
  marca_codigo: string | null;
  marca_nombre: string | null;
  cliente_codigo: string | null;
  gama_nombre: string | null;
  categoria_nombre: string | null;
  subcategoria_nombre: string | null;
};

type Draft = {
  gama_id: string | null;
  categoria_id: string | null;
  subcategoria_id: string | null;
  foc_target_pct: number | null;
  margen_pct: number | null;
  comision_pm_pct: number | null;
  rappel_pct: number | null;
  marketing_pct: number | null;
  financiero_pct: number | null;
  margen_target_pvpr_pct: number | null;
};

const EMPTY_DRAFT: Draft = {
  gama_id: null, categoria_id: null, subcategoria_id: null,
  foc_target_pct: null, margen_pct: null, comision_pm_pct: null,
  rappel_pct: null, marketing_pct: null, financiero_pct: null,
  margen_target_pvpr_pct: null,
};

const CLIENTE_GAME = 'GAME';

const fmtPctEdit = (n: number | string | null): string => {
  // PostgreSQL numeric viaja como string en node-pg → coerce a Number antes de validar.
  if (n == null || n === '') return '';
  const num = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(num)) return '';
  return Number(num.toFixed(2)).toString();
};

const parsePctInput = (s: string): number | null => {
  if (s === '' || s === '-') return null;
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) ? Number(n.toFixed(2)) : null;
};

// ───────────────────────────────────────────────────────────────────────
// Componente
// ───────────────────────────────────────────────────────────────────────

export function GameParametros() {
  const qc = useQueryClient();

  // Datos auxiliares
  const marcas = useQuery({
    queryKey: ['list', '/admin/marcas'],
    queryFn: () => api.get<{ data: { id: string; codigo: string; nombre: string; cliente_id: string }[] }>('/admin/marcas'),
  });
  const clientes = useQuery({
    queryKey: ['list', '/admin/clientes'],
    queryFn: () => api.get<{ data: { id: string; codigo: string }[] }>('/admin/clientes'),
  });
  const gamas = useQuery({
    queryKey: ['list', '/admin/gamas'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; orden: number }[] }>('/admin/gamas'),
  });
  const categorias = useQuery({
    queryKey: ['list', '/admin/categorias'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/categorias'),
  });
  const subcategorias = useQuery({
    queryKey: ['list', '/admin/subcategorias'],
    queryFn: () => api.get<{ data: { id: string; nombre: string; categoria_id: string }[] }>('/admin/subcategorias'),
  });

  // Resolver marca GAME (única que existe para el cliente GAME por ahora)
  const marcaGame = useMemo(() => {
    if (!marcas.data?.data || !clientes.data?.data) return null;
    const cli = clientes.data.data.find((c) => c.codigo === CLIENTE_GAME);
    if (!cli) return null;
    return marcas.data.data.find((m) => m.cliente_id === cli.id && m.codigo === CLIENTE_GAME) ?? null;
  }, [marcas.data, clientes.data]);

  // Listado
  const lista = useQuery({
    queryKey: ['condiciones-comerciales', CLIENTE_GAME],
    queryFn: () => api.get<{ data: CondicionRow[] }>(`/admin/condiciones-comerciales/listado/full?cliente_codigo=${CLIENTE_GAME}`),
  });

  // Parametros globales (video + traduccion + num tiendas)
  const videoParam = useQuery({
    queryKey: ['param', 'video_nuevos_productos_eur'],
    queryFn: () => api.get<{ clave: string; valor: string }>('/parametros/video_nuevos_productos_eur'),
  });
  const tradParam = useQuery({
    queryKey: ['param', 'traduccion_nuevo_producto_eur'],
    queryFn: () => api.get<{ clave: string; valor: string }>('/parametros/traduccion_nuevo_producto_eur'),
  });
  const numTiendasParam = useQuery({
    queryKey: ['param', 'num_tiendas_game'],
    queryFn: () => api.get<{ clave: string; valor: string }>('/parametros/num_tiendas_game'),
  });

  const updateParam = useMutation({
    mutationFn: ({ clave, valor }: { clave: string; valor: string }) =>
      api.put(`/admin/parametros/${clave}`, { valor }),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['param', vars.clave] });
    },
  });

  // ───────── Estado edición filas ─────────

  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);

  const toNum = (v: unknown): number | null => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const startEdit = (row: CondicionRow) => {
    setEditingId(row.id);
    setCreating(false);
    setDraft({
      gama_id: row.gama_id,
      categoria_id: row.categoria_id,
      subcategoria_id: row.subcategoria_id,
      foc_target_pct: toNum(row.foc_target_pct),
      margen_pct: toNum(row.margen_pct),
      comision_pm_pct: toNum(row.comision_pm_pct),
      rappel_pct: toNum(row.rappel_pct),
      marketing_pct: toNum(row.marketing_pct),
      financiero_pct: toNum(row.financiero_pct),
      margen_target_pvpr_pct: toNum(row.margen_target_pvpr_pct),
    });
  };

  const startCreate = () => {
    setEditingId(null);
    setCreating(true);
    setDraft(EMPTY_DRAFT);
  };

  const cancel = () => {
    setEditingId(null);
    setCreating(false);
    setDraft(EMPTY_DRAFT);
  };

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/admin/condiciones-comerciales', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['condiciones-comerciales', CLIENTE_GAME] });
      cancel();
    },
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.put(`/admin/condiciones-comerciales/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['condiciones-comerciales', CLIENTE_GAME] });
      cancel();
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/condiciones-comerciales/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['condiciones-comerciales', CLIENTE_GAME] }),
  });

  const save = async () => {
    if (creating) {
      if (!marcaGame) { toast.error('No se encontró la marca GAME'); return; }
      await create.mutateAsync({ marca_id: marcaGame.id, ...draft });
    } else if (editingId) {
      await update.mutateAsync({ id: editingId, body: draft });
    }
  };

  // Filtrar subcategorías según categoría elegida en el draft
  const subcatsForDraft = useMemo(() => {
    if (!subcategorias.data?.data) return [];
    if (!draft.categoria_id) return subcategorias.data.data;
    return subcategorias.data.data.filter((s) => s.categoria_id === draft.categoria_id);
  }, [draft.categoria_id, subcategorias.data]);

  if (lista.isLoading || marcas.isLoading || clientes.isLoading) return <Spinner />;
  const rows = lista.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Parámetros GAME"
        subtitle="Condiciones comerciales aplicables al cliente GAME, por combinación de Gama / Categoría / Subcategoría."
      />

      {/* Parámetros globales arriba */}
      <Card title="Importes fijos para nuevos productos">
        <div className="grid gap-4 sm:grid-cols-2">
          <InlineEuro
            label="Video nuevos productos"
            value={videoParam.data?.valor}
            loading={videoParam.isLoading || updateParam.isPending}
            onSave={(v) => updateParam.mutate({ clave: 'video_nuevos_productos_eur', valor: v })}
          />
          <InlineEuro
            label="Traducción nuevo producto"
            value={tradParam.data?.valor}
            loading={tradParam.isLoading || updateParam.isPending}
            onSave={(v) => updateParam.mutate({ clave: 'traduccion_nuevo_producto_eur', valor: v })}
          />
        </div>
      </Card>

      <div className="h-4" />

      <Card title="Red de tiendas GAME">
        <div className="grid gap-4 sm:grid-cols-2">
          <InlineInteger
            label="Nº de tiendas"
            value={numTiendasParam.data?.valor}
            loading={numTiendasParam.isLoading || updateParam.isPending}
            onSave={(v) => updateParam.mutate({ clave: 'num_tiendas_game', valor: v })}
          />
        </div>
      </Card>

      <div className="my-4 rounded-md bg-amber-50 px-4 py-2 text-xs text-amber-800 ring-1 ring-amber-200">
        * F.O.C. TARGET = %target de F.O.C. que vamos a ofrecer al cliente, para tenerlo en cuenta de cara al cálculo de tarifas.
      </div>

      <Card
        title="Condiciones por combinación"
        description={`Marca GAME (implícita). Selecciona "Todos" en cualquier columna para aplicar a todo el grupo.`}
        actions={
          !creating && !editingId && (
            <Button variant="create" onClick={startCreate}>+ Nueva fila</Button>
          )
        }
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <Th>Gama</Th>
                <Th>Categoría</Th>
                <Th>Subcategoría</Th>
                <Th align="right">F.O.C. target *</Th>
                <Th align="right">Margen Approx</Th>
                <Th align="right">Comisión PM</Th>
                <Th align="right">Rappel</Th>
                <Th align="right">MKT</Th>
                <Th align="right">% financiero</Th>
                <ThEmphasis align="right">Margen target PVPR cliente</ThEmphasis>
                <Th align="right">Acciones</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {creating && (
                <EditRow
                  draft={draft}
                  setDraft={setDraft}
                  gamas={gamas.data?.data ?? []}
                  categorias={categorias.data?.data ?? []}
                  subcategorias={subcatsForDraft}
                  onSave={save}
                  onCancel={cancel}
                  saving={create.isPending}
                />
              )}
              {rows.map((row) =>
                editingId === row.id ? (
                  <EditRow
                    key={row.id}
                    draft={draft}
                    setDraft={setDraft}
                    gamas={gamas.data?.data ?? []}
                    categorias={categorias.data?.data ?? []}
                    subcategorias={subcatsForDraft}
                    onSave={save}
                    onCancel={cancel}
                    saving={update.isPending}
                  />
                ) : (
                  <ViewRow
                    key={row.id}
                    row={row}
                    onEdit={() => startEdit(row)}
                    onDelete={async () => {
                      if (await confirmar({ title: '¿Eliminar esta línea de condiciones?', confirmLabel: 'Eliminar', danger: true })) del.mutate(row.id);
                    }}
                    disabled={!!editingId || creating}
                  />
                ),
              )}
              {rows.length === 0 && !creating && (
                <tr><td colSpan={11} className="px-3 py-4 text-center text-xs text-slate-400">Sin condiciones. Crea la primera con &quot;+ Nueva fila&quot;.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Subcomponentes
// ───────────────────────────────────────────────────────────────────────

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <th className={`px-3 py-2 text-${align} text-xs font-semibold uppercase tracking-wide text-slate-600`}>{children}</th>;
}

function ThEmphasis({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <th className={`bg-emerald-100 px-3 py-2 text-${align} text-xs font-bold uppercase tracking-wide text-emerald-800 ring-1 ring-emerald-200`}>{children}</th>;
}

function TdEmphasis({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <td className={`bg-emerald-50/60 px-3 py-2 text-${align} text-sm font-semibold text-emerald-900`}>{children}</td>;
}

function Td({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <td className={`px-3 py-2 text-${align} text-sm text-slate-700`}>{children}</td>;
}

function InlineEuro({ label, value, onSave, loading }: {
  label: string;
  value: string | undefined;
  onSave: (v: string) => void;
  loading: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  if (!editing) {
    return (
      <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm text-slate-900">{fmt.eur(value ?? null)}</span>
          <Button size="sm" variant="edit" onClick={() => { setDraft(value ?? ''); setEditing(true); }}>✏️ Editar</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between rounded-md bg-amber-50 px-3 py-2 ring-1 ring-amber-200">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="flex items-center gap-2">
        <Input type="number" step="0.01" value={draft} onChange={(e) => setDraft(e.target.value)} className="w-32 text-right" />
        <Button size="sm" variant="save" disabled={loading} onClick={() => { onSave(draft); setEditing(false); }}>Guardar</Button>
        <Button size="sm" variant="danger" onClick={() => setEditing(false)}>Cancelar</Button>
      </div>
    </div>
  );
}

function InlineInteger({ label, value, onSave, loading }: {
  label: string;
  value: string | undefined;
  onSave: (v: string) => void;
  loading: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const display = value != null && value !== '' && Number.isFinite(Number(value)) ? fmt.int(Number(value)) : '—';
  if (!editing) {
    return (
      <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm text-slate-900">{display}</span>
          <Button size="sm" variant="edit" onClick={() => { setDraft(value ?? ''); setEditing(true); }}>✏️ Editar</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between rounded-md bg-amber-50 px-3 py-2 ring-1 ring-amber-200">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          step="1"
          min="0"
          value={draft}
          onChange={(e) => setDraft(e.target.value.replace(/[^\d-]/g, ''))}
          className="w-24 text-right"
        />
        <Button
          size="sm"
          variant="save"
          disabled={loading}
          onClick={() => {
            const n = Number.parseInt(draft, 10);
            const safe = Number.isFinite(n) ? String(n) : '0';
            onSave(safe);
            setEditing(false);
          }}
        >Guardar</Button>
        <Button size="sm" variant="danger" onClick={() => setEditing(false)}>Cancelar</Button>
      </div>
    </div>
  );
}

function PctInput({ value, onChange }: { value: number | string | null; onChange: (v: number | null) => void }) {
  return (
    <Input
      type="number"
      step="0.01"
      value={fmtPctEdit(value)}
      onChange={(e) => onChange(parsePctInput(e.target.value))}
      className="w-20 text-right"
    />
  );
}

function ViewRow({ row, onEdit, onDelete, disabled }: { row: CondicionRow; onEdit: () => void; onDelete: () => void; disabled: boolean }) {
  return (
    <tr>
      <Td>{row.gama_nombre ?? <span className="italic text-slate-400">Todos</span>}</Td>
      <Td>{row.categoria_nombre ?? <span className="italic text-slate-400">Todos</span>}</Td>
      <Td>{row.subcategoria_nombre ?? <span className="italic text-slate-400">Todos</span>}</Td>
      <Td align="right">{fmt.pct(row.foc_target_pct)}</Td>
      <Td align="right">{fmt.pct(row.margen_pct)}</Td>
      <Td align="right">{fmt.pct(row.comision_pm_pct)}</Td>
      <Td align="right">{fmt.pct(row.rappel_pct)}</Td>
      <Td align="right">{fmt.pct(row.marketing_pct)}</Td>
      <Td align="right">{fmt.pct(row.financiero_pct)}</Td>
      <TdEmphasis align="right">{fmt.pct(row.margen_target_pvpr_pct)}</TdEmphasis>
      <Td align="right">
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="edit" disabled={disabled} onClick={onEdit}>✏️ Editar</Button>
          <Button size="sm" variant="danger" disabled={disabled} onClick={onDelete}>🗑 Eliminar</Button>
        </div>
      </Td>
    </tr>
  );
}

function EditRow({ draft, setDraft, gamas, categorias, subcategorias, onSave, onCancel, saving }: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  gamas: { id: string; nombre: string }[];
  categorias: { id: string; nombre: string }[];
  subcategorias: { id: string; nombre: string }[];
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const setF = <K extends keyof Draft>(key: K, val: Draft[K]) => setDraft({ ...draft, [key]: val });

  return (
    <tr className="bg-amber-50">
      <Td>
        <Select
          options={[{ value: '', label: 'Todos' }, ...gamas.map((g) => ({ value: g.id, label: g.nombre }))]}
          value={draft.gama_id ?? ''}
          emptyLabel={null}
          onChange={(e) => setF('gama_id', e.target.value || null)}
        />
      </Td>
      <Td>
        <Select
          options={[{ value: '', label: 'Todos' }, ...categorias.map((c) => ({ value: c.id, label: c.nombre }))]}
          value={draft.categoria_id ?? ''}
          emptyLabel={null}
          onChange={(e) => setDraft({ ...draft, categoria_id: e.target.value || null, subcategoria_id: null })}
        />
      </Td>
      <Td>
        <Select
          options={[{ value: '', label: 'Todos' }, ...subcategorias.map((s) => ({ value: s.id, label: s.nombre }))]}
          value={draft.subcategoria_id ?? ''}
          emptyLabel={null}
          onChange={(e) => setF('subcategoria_id', e.target.value || null)}
        />
      </Td>
      <Td align="right"><PctInput value={draft.foc_target_pct} onChange={(v) => setF('foc_target_pct', v)} /></Td>
      <Td align="right"><PctInput value={draft.margen_pct} onChange={(v) => setF('margen_pct', v)} /></Td>
      <Td align="right"><PctInput value={draft.comision_pm_pct} onChange={(v) => setF('comision_pm_pct', v)} /></Td>
      <Td align="right"><PctInput value={draft.rappel_pct} onChange={(v) => setF('rappel_pct', v)} /></Td>
      <Td align="right"><PctInput value={draft.marketing_pct} onChange={(v) => setF('marketing_pct', v)} /></Td>
      <Td align="right"><PctInput value={draft.financiero_pct} onChange={(v) => setF('financiero_pct', v)} /></Td>
      <TdEmphasis align="right"><PctInput value={draft.margen_target_pvpr_pct} onChange={(v) => setF('margen_target_pvpr_pct', v)} /></TdEmphasis>
      <Td align="right">
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="save" disabled={saving} onClick={onSave}>Guardar</Button>
          <Button size="sm" variant="danger" onClick={onCancel}>Cancelar</Button>
        </div>
      </Td>
    </tr>
  );
}
